/**
 * Shared date utilities for Fitxess app
 * SINGLE SOURCE OF TRUTH for all week calculations
 * All weeks start on SUNDAY (weekStartsOn: 0)
 */

/**
 * Normalize a date to local midday to prevent timezone off-by-one issues
 * @param {Date|string} date - Date object or YYYY-MM-DD string
 * @returns {Date} - Date normalized to 12:00:00 local time
 */
export function normalizeDate(date) {
  let d;
  if (typeof date === 'string') {
    // Parse YYYY-MM-DD string safely
    const parts = date.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
    const day = parseInt(parts[2], 10);
    d = new Date(year, month, day, 12, 0, 0);
  } else {
    d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
  }
  return d;
}

/**
 * Get the start of the week (Sunday) for a given date
 * @param {Date|string} date - Date object or YYYY-MM-DD string
 * @returns {Date} - Sunday of that week at 12:00:00 local time
 */
export function getWeekStart(date) {
  const normalized = normalizeDate(date);
  const dayOfWeek = normalized.getDay(); // 0 = Sunday, 6 = Saturday
  const weekStart = new Date(normalized);
  weekStart.setDate(normalized.getDate() - dayOfWeek);
  return weekStart;
}

/**
 * Get the end of the week (Saturday) for a given date
 * @param {Date|string} date - Date object or YYYY-MM-DD string
 * @returns {Date} - Saturday of that week at 12:00:00 local time
 */
export function getWeekEnd(date) {
  const weekStart = getWeekStart(date);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  return weekEnd;
}

/**
 * Get all 7 dates in a week as YYYY-MM-DD strings (Sunday to Saturday)
 * @param {Date|string} date - Any date within the target week
 * @returns {string[]} - Array of 7 date strings [Sun, Mon, Tue, Wed, Thu, Fri, Sat]
 */
export function getWeekDatesArray(date) {
  const weekStart = getWeekStart(date);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    dates.push(formatDateString(d));
  }
  return dates;
}

/**
 * Get week dates with an offset from current week
 * @param {number} offset - Week offset (0 = current week, -1 = last week, +1 = next week)
 * @returns {string[]} - Array of 7 date strings
 */
export function getWeekDatesWithOffset(offset) {
  const now = normalizeDate(new Date());
  const targetDate = new Date(now);
  targetDate.setDate(now.getDate() + (offset * 7));
  return getWeekDatesArray(targetDate);
}

/**
 * Format a Date object as YYYY-MM-DD string
 * @param {Date} date - Date object
 * @returns {string} - YYYY-MM-DD formatted string
 */
export function formatDateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get formatted week range text (e.g., "Feb 8 - 14, 2026")
 * @param {string[]} weekDates - Array of 7 date strings
 * @returns {string} - Formatted range string
 */
export function getWeekRangeText(weekDates) {
  if (!weekDates || weekDates.length < 7) return '';
  
  const start = normalizeDate(weekDates[0]);
  const end = normalizeDate(weekDates[6]);
  
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  
  if (startMonth === endMonth) {
    return `${startMonth} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
  }
  return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${end.getFullYear()}`;
}

/**
 * Check if a week offset represents the current week
 * @param {number} offset - Week offset
 * @returns {boolean}
 */
export function isCurrentWeek(offset) {
  return offset === 0;
}

/**
 * Get today's date as YYYY-MM-DD string
 * @returns {string}
 */
export function getTodayString() {
  return formatDateString(normalizeDate(new Date()));
}

/**
 * Resolve the date a log should be written under, unifying "live current-day" logging with
 * "intentionally selected fixed date" (calendar / planning / history) flows.
 *
 * A LIVE current-day flow uses `liveToday` (a reactive value from useToday()) so a log written
 * after local midnight lands on the new day — matching what Home displays. An explicit fixed
 * date is preserved exactly.
 *
 * @param {Object} opts
 * @param {string|null} opts.explicitDate      - the `?date=` query param, if any (YYYY-MM-DD)
 * @param {string|boolean|null} opts.currentFlag - the `?current=` query param ('1' => live current-day)
 * @param {string} opts.liveToday              - reactive local today (YYYY-MM-DD) from useToday()
 * @param {boolean} [opts.missingMeansCurrent] - when true, absence of explicitDate also means live current-day
 * @returns {string} YYYY-MM-DD
 */
export function resolveLoggingDate(opts) {
  const { explicitDate, currentFlag, liveToday, missingMeansCurrent = false } = opts || {};
  const isCurrent = currentFlag === '1' || currentFlag === true
    || (missingMeansCurrent && !explicitDate);
  return isCurrent ? liveToday : (explicitDate || liveToday);
}


/**
 * Ordinal suffix for a day-of-month number (1st, 2nd, 3rd, 4th, 11th, 21st…).
 * @param {number} n
 * @returns {string} - the number with its ordinal suffix, e.g. "29th"
 */
export function getOrdinal(n) {
  var s = ['th', 'st', 'nd', 'rd'];
  var v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Full, human date with ordinal day, e.g. "Tuesday, September 29th".
 * Timezone-safe (parses YYYY-MM-DD at local midday via normalizeDate).
 * @param {Date|string} dateInput
 * @returns {string}
 */
export function formatFullDateWithOrdinal(dateInput) {
  var d = normalizeDate(dateInput);
  var weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
  var month = d.toLocaleDateString('en-US', { month: 'long' });
  return `${weekday}, ${month} ${getOrdinal(d.getDate())}`;
}

/**
 * Compact, human date, e.g. "Sep 29th". Timezone-safe.
 * @param {Date|string} dateInput
 * @returns {string}
 */
export function formatFriendlyDate(dateInput) {
  var d = normalizeDate(dateInput);
  var month = d.toLocaleDateString('en-US', { month: 'short' });
  return `${month} ${getOrdinal(d.getDate())}`;
}

/**
 * Get formatted day label (e.g., "Sun 8")
 * @param {string} dateStr - YYYY-MM-DD date string
 * @returns {{ day: string, date: number }}
 */
export function getDayLabel(dateStr) {
  const d = normalizeDate(dateStr);
  const day = d.toLocaleDateString('en-US', { weekday: 'short' });
  const date = d.getDate();
  return { day, date };
}
